import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Image } from "primereact/image";
import { Layer, Stage } from "react-konva";
import ProblemPreviewMarking from "./ProblemPreviewMarking";
import { getProblemFoto } from "../../utils/problemImage";
import { getMarkingReferenceImage } from "../../../cek-keluar-masuk/utils/getMarkingReferenceImage";

// height / width of the model until its image is loaded
const defaultHeightRatio = 9 / 16;

// The 2D model the problem was marked on, with the marker on it. Mounted only while the dialog is open, it measures
// its own width (and follows it while the dialog opens / the screen resizes).
// The model keeps the proportions of its own image (the marker is a fraction of the image, so it stays on the same spot).
const ReferenceModel = ({ imageUrl, marking }) => {
  const bgDivRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [heightRatio, setHeightRatio] = useState(defaultHeightRatio);

  useEffect(() => {
    const element = bgDivRef.current;
    if (!element) return undefined;

    const measure = () =>
      setWidth(Math.round(element.getBoundingClientRect().width));
    measure();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const height = Math.round(width * heightRatio);

  return (
    <div ref={bgDivRef} className="relative bg-red-100 w-full">
      <img
        src={imageUrl}
        alt="model referensi"
        className="block w-full h-auto"
        onLoad={(e) => {
          const { naturalWidth, naturalHeight } = e.currentTarget;
          if (naturalWidth > 0 && naturalHeight > 0) {
            setHeightRatio(naturalHeight / naturalWidth);
          }
        }}
      />
      {width > 50 && (
        <div className="absolute top-0 left-0">
          <Stage width={width} height={height}>
            <Layer>
              <ProblemPreviewMarking
                parent_width={width}
                parent_height={height}
                data={{ marking_kordinat: marking }}
              />
            </Layer>
          </Stage>
        </div>
      )}
    </div>
  );
};

// The image button of a problem: the Foto Asli (the photo of the problem) and, when the problem has a marker, the
// Model Referensi with the marker on it. Without a marker (or without a model to put it on) there is no Model Referensi.
const ProblemPreviewImageDialog = ({ visible, onHide, data }) => {
  const problem = data ?? {};
  const marking = problem.marking_kordinat;
  const foto = getProblemFoto(problem);

  // the model of the card the problem was marked on (label_grup is gambar_depan, gambar_box_kiri, ...)
  const modelUrl =
    marking && problem.label_grup
      ? getMarkingReferenceImage({
          headerData: {
            data_kendaraan: problem.data_kendaraan,
            data_trailer_berangkat: problem.data_trailer,
            data_trailer_pulang: problem.data_trailer,
          },
          field: problem.label_grup,
          type: "berangkat",
        })
      : "";

  return (
    <Dialog
      visible={visible}
      // wider with a Model Referensi: the two images go side by side
      style={{ width: modelUrl ? "64rem" : "45rem" }}
      breakpoints={{ "960px": "75vw", "641px": "90vw" }}
      header="Preview Gambar"
      modal
      className="p-fluid"
      onHide={onHide}
    >
      <div className="col-start-start gap-4">
        {/* Foto Asli and Model Referensi: side by side on a wide screen, under each other on a small one */}
        <div
          className={`grid grid-cols-1 gap-4 w-full ${
            modelUrl ? "lg:grid-cols-2" : ""
          }`}
        >
          <div className="col-start-start gap-1 w-full min-w-0">
            <span className="text-xs font-semibold text-slate-400">
              Foto Asli
            </span>
            {foto ? (
              // preview: tap the photo to see it full screen (zoom / rotate)
              <Image
                src={foto}
                alt="foto asli"
                width="100%"
                className="w-full bg-white shadow-md"
                imageClassName="w-full rounded"
                preview
              />
            ) : (
              <div className="row-center-center bg-slate-100 text-slate-400 text-sm italic w-full h-[6rem] rounded">
                Belum ada foto
              </div>
            )}
          </div>

          {modelUrl && (
            <div className="col-start-start gap-1 w-full min-w-0">
              <span className="text-xs font-semibold text-slate-400">
                Model Referensi
              </span>
              {/* key: a new problem measures and draws again */}
              {visible && (
                <ReferenceModel
                  key={problem.id}
                  imageUrl={modelUrl}
                  marking={marking}
                />
              )}
            </div>
          )}
        </div>

        <span className="font-semibold">{problem.keterangan}</span>
      </div>
    </Dialog>
  );
};

export default ProblemPreviewImageDialog;
